import {
	buildReserveRows,
	formatCycles,
	loadCycleSnapshotFile,
	shortCanisterId,
} from "@/lib/cycles/load-snapshots";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

/**
 * Daily cycle balance + day-over-day spend from ops snapshots.
 * Complements Ops ICP Gas Station funding UI on /cycles.
 */
export function CycleReservesPanel() {
	const { data, isLoading, isError, isFetching, refetch } = useQuery({
		queryKey: ["cycleSnapshotReserves"],
		queryFn: loadCycleSnapshotFile,
		staleTime: 60_000,
	});

	const view = buildReserveRows(data ?? null);
	const updated =
		view.updatedAt != null
			? new Date(view.updatedAt).toISOString().replace("T", " ").slice(0, 16)
			: "—";

	return (
		<section className="card-surface space-y-4 p-5">
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<p className="section-eyebrow">Reserves</p>
					<h2 className="text-lg font-semibold tracking-tight">
						Canister cycle balances
					</h2>
					<p className="mt-1 max-w-xl text-xs text-muted-foreground">
						Daily snapshots ({view.network} · {view.points} points). Spend =
						previous snapshot − current (positive means cycles consumed). Live
						blackhole status may supplement when controllers are set.
					</p>
				</div>
				<button
					type="button"
					className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-muted/40"
					onClick={() => void refetch()}
					disabled={isFetching}
				>
					{isFetching ? "Refreshing…" : "Refresh"}
				</button>
			</div>

			{isLoading && <div className="skeleton-shimmer h-24" />}

			{isError && (
				<p className="text-sm text-danger">
					Could not load cycle snapshots. Ops should run{" "}
					<code className="text-[11px]">
						ICP_ENVIRONMENT=play node scripts/record-cycle-snapshots.mjs
					</code>
					.
				</p>
			)}

			{!isLoading && !isError && view.rows.length === 0 && (
				<p className="text-sm text-muted-foreground">
					No snapshot file yet. Record daily balances so spend charts can start.
				</p>
			)}

			{view.rows.length > 0 && (
				<>
					<div className="grid gap-3 sm:grid-cols-3">
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Total reserves
							</p>
							<p className="font-mono text-sm font-semibold">
								{formatCycles(view.totalBalance)}
							</p>
						</div>
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Period spend (Σ)
							</p>
							<p
								className={cn(
									"font-mono text-sm font-semibold",
									view.totalSpend != null && view.totalSpend > 0n
										? "text-warning"
										: "text-muted-foreground",
								)}
							>
								{view.totalSpend == null
									? "—"
									: view.totalSpend > 0n
										? `−${formatCycles(view.totalSpend)}`
										: view.totalSpend < 0n
											? `+${formatCycles(-view.totalSpend)}`
											: "≈ 0"}
							</p>
						</div>
						<div className="rounded-xl border border-border/60 bg-muted/20 px-3 py-2">
							<p className="text-[10px] font-semibold uppercase text-muted-foreground">
								Last snapshot (UTC)
							</p>
							<p className="font-mono text-sm font-semibold">{updated}Z</p>
						</div>
					</div>

					<div className="overflow-x-auto rounded-xl border border-border/60">
						<table className="w-full min-w-[28rem] text-left text-xs">
							<thead className="bg-muted/30 text-[10px] uppercase text-muted-foreground">
								<tr>
									<th className="px-3 py-2 font-semibold">Canister</th>
									<th className="px-3 py-2 font-semibold">Principal</th>
									<th className="px-3 py-2 font-semibold text-right">Balance</th>
									<th className="px-3 py-2 font-semibold text-right">
										Δ spend
									</th>
								</tr>
							</thead>
							<tbody>
								{view.rows.map((row) => (
									<tr
										key={row.canisterId}
										className="border-t border-border/40"
									>
										<td className="px-3 py-2 font-medium">{row.label}</td>
										<td className="px-3 py-2 font-mono text-muted-foreground">
											{shortCanisterId(row.canisterId)}
										</td>
										<td className="px-3 py-2 text-right font-mono">
											{formatCycles(row.balance)}
										</td>
										<td
											className={cn(
												"px-3 py-2 text-right font-mono",
												row.spend != null && row.spend > 0n
													? "text-warning"
													: "text-muted-foreground",
											)}
										>
											{row.spend == null
												? "—"
												: row.spend > 0n
													? `−${formatCycles(row.spend)}`
													: row.spend < 0n
														? `+${formatCycles(-row.spend)}`
														: "0"}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</section>
	);
}
